import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  message,
  Space,
  Tag,
  Card,
  Descriptions,
  Divider,
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { apiClient } from '../../../config/apiClient';
import MainLayout from '../../layout/layout';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { TextArea } = Input;

const MovimientosStock = () => {
  const [movimientos, setMovimientos] = useState([]);
  const [articulos, setArticulos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalDetalleVisible, setModalDetalleVisible] = useState(false);
  const [movimientoSeleccionado, setMovimientoSeleccionado] = useState(null);
  const [comprobanteDetalle, setComprobanteDetalle] = useState(null);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [form] = Form.useForm();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [filtroArticulo, setFiltroArticulo] = useState(null);
  const [fechaRango, setFechaRango] = useState([dayjs().startOf('month'), dayjs()]);

  const loadMovimientos = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filtroArticulo) {
        params.append('articulo_id', filtroArticulo);
      }
      if (fechaRango[0]) {
        params.append('fecha_desde', fechaRango[0].format('YYYY-MM-DD'));
      }
      if (fechaRango[1]) {
        params.append('fecha_hasta', fechaRango[1].format('YYYY-MM-DD'));
      }
      
      const response = await apiClient.get(`/gestion/movimientos-stock?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setMovimientos(data);
      } else {
        message.error('Error al cargar los movimientos');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al cargar los movimientos');
    } finally {
      setLoading(false);
    }
  };

  const loadArticulos = async () => {
    try {
      const response = await apiClient.get('/gestion/articulos');
      if (response.ok) {
        const data = await response.json();
        setArticulos(data.filter(a => a.art_activo && a.art_mueve_stock));
      }
    } catch (error) {
      console.error('Error al cargar artículos:', error);
    }
  };

  useEffect(() => {
    loadArticulos();
  }, []);

  // Recargar movimientos cuando cambian los filtros (así la grilla usa el estado ya actualizado)
  useEffect(() => {
    loadMovimientos();
  }, [filtroArticulo, fechaRango]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleOpenModal = () => {
    form.resetFields();
    form.setFieldsValue({
      tipo_movimiento: 'ajuste',
    });
    setModalVisible(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const dataToSend = {
        articulo_id: values.articulo_id,
        cantidad: values.cantidad,
        tipo_movimiento: values.tipo_movimiento || 'ajuste',
        observaciones: values.observaciones || null,
      };

      const response = await apiClient.post('/gestion/movimientos-stock/ajuste', dataToSend);
      if (response.ok) {
        message.success('Ajuste de stock registrado correctamente');
        setModalVisible(false);
        loadMovimientos();
        loadArticulos();
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al registrar el ajuste');
      }
    } catch (error) {
      if (error.errorFields) return;
      console.error('Error:', error);
      message.error('Error al guardar el ajuste');
    }
  };

  const handleVerDetalle = async (movimiento) => {
    setMovimientoSeleccionado(movimiento);
    setModalDetalleVisible(true);
    setComprobanteDetalle(null);

    if (movimiento.mos_tipo_referencia === 'compra' && movimiento.mos_referencia_id) {
      setLoadingDetalle(true);
      try {
        const response = await apiClient.get(`/gestion/compras/${movimiento.mos_referencia_id}`);
        if (response.ok) {
          const data = await response.json();
          setComprobanteDetalle({ tipo: 'compra', ...data });
        } else {
          message.error('No se pudo cargar el detalle de la compra');
        }
      } catch (error) {
        console.error('Error:', error);
        message.error('Error al cargar el detalle');
      } finally {
        setLoadingDetalle(false);
      }
    } else if (movimiento.mos_tipo_referencia === 'venta' && movimiento.mos_referencia_id) {
      setLoadingDetalle(true);
      try {
        const response = await apiClient.get(`/gestion/ventas/${movimiento.mos_referencia_id}`);
        if (response.ok) {
          const data = await response.json();
          setComprobanteDetalle({ tipo: 'venta', ...data });
        } else {
          message.error('No se pudo cargar el detalle de la venta');
        }
      } catch (error) {
        console.error('Error:', error);
        message.error('Error al cargar el detalle');
      } finally {
        setLoadingDetalle(false);
      }
    }
  };

  const columns = [
    {
      title: 'Fecha',
      dataIndex: 'mos_fecha',
      key: 'mos_fecha',
      render: (fecha) => dayjs(fecha).format('DD/MM/YYYY HH:mm'),
    },
    {
      title: 'Artículo',
      dataIndex: 'articulo_nombre',
      key: 'articulo_nombre',
    },
    {
      title: 'Tipo',
      dataIndex: 'mos_tipo_movimiento',
      key: 'mos_tipo_movimiento',
      render: (tipo) => {
        const colors = {
          compra: 'green',
          venta: 'red',
          ajuste: 'blue',
        };
        return (
          <Tag color={colors[tipo] || 'default'}>
            {tipo === 'compra' ? <ArrowUpOutlined /> : tipo === 'venta' ? <ArrowDownOutlined /> : '⚙️'} {tipo.toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: 'Nº Comprobante',
      dataIndex: 'numero_comprobante',
      key: 'numero_comprobante',
      render: (v) => v || '-',
    },
    {
      title: 'Cantidad',
      dataIndex: 'mos_cantidad',
      key: 'mos_cantidad',
      render: (cantidad) => (
        <span style={{ color: cantidad > 0 ? '#52c41a' : '#ff4d4f', fontWeight: 'bold' }}>
          {cantidad > 0 ? '+' : ''}{cantidad}
        </span>
      ),
    },
    {
      title: 'Observaciones',
      dataIndex: 'mos_observaciones',
      key: 'mos_observaciones',
      render: (obs) => obs || '-',
    },
    {
      title: '',
      key: 'accion',
      width: 80,
      align: 'center',
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleVerDetalle(record)}
          size="small"
        >
          Ver
        </Button>
      ),
    },
  ];

  return (
    <MainLayout>
      <div style={{ padding: '24px' }}>
        <div style={{ marginBottom: 16 }}>
          <h1 style={{ margin: 0, marginBottom: 16, color: '#ffffff', fontSize: isMobile ? '20px' : '24px' }}>
            Movimientos de Stock
          </h1>
          <Space wrap style={{ marginBottom: 16 }}>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadMovimientos}
              loading={loading}
            >
              {isMobile ? null : 'Actualizar'}
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleOpenModal}
            >
              Nuevo Ajuste
            </Button>
          </Space>

          {/* Filtros */}
          <div style={{
            marginBottom: 16,
            padding: 16,
            backgroundColor: '#2d2d2d',
            borderRadius: 8,
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: 12,
            alignItems: isMobile ? 'stretch' : 'flex-end'
          }}>
            <div style={{ flex: 1, minWidth: isMobile ? '100%' : '200px' }}>
              <label style={{ display: 'block', marginBottom: 4, color: '#ffffff', fontSize: '14px' }}>
                Filtrar por artículo
              </label>
              <Select
                placeholder="Todos los artículos"
                value={filtroArticulo}
                onChange={(value) => setFiltroArticulo(value)}
                allowClear
                showSearch
                style={{ width: '100%' }}
                filterOption={(input, option) => {
                  const text = String(option?.children || option?.label || '');
                  return text.toLowerCase().includes(input.toLowerCase());
                }}
              >
                {articulos.map(art => (
                  <Select.Option key={art.art_id} value={art.art_id}>
                    {art.art_nombre}
                  </Select.Option>
                ))}
              </Select>
            </div>
            <div style={{ flex: 1, minWidth: isMobile ? '100%' : '300px' }}>
              <label style={{ display: 'block', marginBottom: 4, color: '#ffffff', fontSize: '14px' }}>
                Rango de fechas
              </label>
              <RangePicker
                value={fechaRango}
                onChange={(dates) => setFechaRango(dates)}
                format="DD/MM/YYYY"
                style={{ width: '100%' }}
              />
            </div>
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={movimientos}
          loading={loading}
          rowKey="mos_id"
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `Total: ${total} movimientos`,
          }}
        />

        {/* Modal para crear ajuste */}
        <Modal
          title="Nuevo Ajuste de Stock"
          open={modalVisible}
          onOk={handleSave}
          onCancel={() => {
            setModalVisible(false);
            form.resetFields();
          }}
          okText="Registrar"
          cancelText="Cancelar"
          width={500}
        >
          <Form form={form} layout="vertical">
            <Form.Item
              name="articulo_id"
              label="Artículo"
              rules={[{ required: true, message: 'Seleccione un artículo' }]}
            >
              <Select
                placeholder="Seleccione un artículo"
                showSearch
                filterOption={(input, option) => {
                  const text = String(option?.children || option?.label || '');
                  return text.toLowerCase().includes(input.toLowerCase());
                }}
              >
                {articulos.map(art => (
                  <Select.Option key={art.art_id} value={art.art_id}>
                    {art.art_nombre} (Stock actual: {art.art_stock_actual})
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="cantidad"
              label="Cantidad"
              rules={[{ required: true, message: 'Ingrese la cantidad' }]}
              help="Valor positivo aumenta stock, negativo lo disminuye"
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="Ej: 10 o -5"
              />
            </Form.Item>

            <Form.Item
              name="tipo_movimiento"
              label="Tipo de Movimiento"
            >
              <Select>
                <Select.Option value="ajuste">Ajuste</Select.Option>
                <Select.Option value="compra">Compra</Select.Option>
                <Select.Option value="venta">Venta</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item name="observaciones" label="Observaciones">
              <TextArea rows={3} placeholder="Motivo del ajuste (opcional)" />
            </Form.Item>
          </Form>
        </Modal>

        {/* Modal para ver detalle del movimiento */}
        <Modal
          title={
            movimientoSeleccionado?.mos_tipo_referencia === 'compra' ? 'Detalle de Compra' :
            movimientoSeleccionado?.mos_tipo_referencia === 'venta' ? 'Detalle de Venta' :
            'Detalle del Movimiento'
          }
          open={modalDetalleVisible}
          onCancel={() => {
            setModalDetalleVisible(false);
            setMovimientoSeleccionado(null);
            setComprobanteDetalle(null);
          }}
          footer={null}
          width={600}
        >
          {loadingDetalle ? (
            <div style={{ padding: 24, textAlign: 'center' }}>Cargando...</div>
          ) : comprobanteDetalle?.tipo === 'compra' ? (
            <>
              <Descriptions column={1} bordered>
                <Descriptions.Item label="Fecha">
                  {dayjs(comprobanteDetalle.com_fecha).format('DD/MM/YYYY')}
                </Descriptions.Item>
                <Descriptions.Item label="Proveedor">
                  {comprobanteDetalle.proveedor_nombre}
                </Descriptions.Item>
                <Descriptions.Item label="Total">
                  {comprobanteDetalle.com_total_comprobante != null
                    ? `$${parseFloat(comprobanteDetalle.com_total_comprobante).toFixed(2)}`
                    : comprobanteDetalle.com_total != null
                    ? `$${parseFloat(comprobanteDetalle.com_total).toFixed(2)}`
                    : '-'}
                </Descriptions.Item>
                {comprobanteDetalle.com_observaciones && (
                  <Descriptions.Item label="Observaciones">
                    {comprobanteDetalle.com_observaciones}
                  </Descriptions.Item>
                )}
              </Descriptions>
              {comprobanteDetalle.formas_pago && comprobanteDetalle.formas_pago.length > 0 && (
                <>
                  <Divider>Formas de Pago</Divider>
                  <Table
                    columns={[
                      { title: 'Forma de Pago', dataIndex: 'forma_pago_nombre', key: 'forma_pago_nombre' },
                      {
                        title: 'Monto',
                        dataIndex: 'cfp_monto',
                        key: 'cfp_monto',
                        align: 'right',
                        render: (monto) => `$${parseFloat(monto).toFixed(2)}`,
                      },
                    ]}
                    dataSource={comprobanteDetalle.formas_pago}
                    pagination={false}
                    size="small"
                  />
                </>
              )}
              <Divider>Artículos</Divider>
              <Table
                columns={[
                  { title: 'Artículo', dataIndex: 'articulo_nombre', key: 'articulo_nombre' },
                  { title: 'Cantidad', dataIndex: 'cod_cantidad', key: 'cod_cantidad', align: 'center' },
                ]}
                dataSource={comprobanteDetalle.detalles || []}
                pagination={false}
                size="small"
              />
            </>
          ) : comprobanteDetalle?.tipo === 'venta' ? (
            <>
              <Descriptions column={1} bordered>
                <Descriptions.Item label="Fecha">
                  {comprobanteDetalle.ven_fecha_creacion
                    ? dayjs(comprobanteDetalle.ven_fecha_creacion.replace(' ', 'T')).format('DD/MM/YYYY HH:mm')
                    : dayjs(comprobanteDetalle.ven_fecha).format('DD/MM/YYYY')}
                </Descriptions.Item>
                <Descriptions.Item label="Método de Pago">
                  <Tag color={(comprobanteDetalle.ven_metodo_pago || '').toLowerCase() === 'efectivo' ? 'green' : (comprobanteDetalle.ven_metodo_pago || '').toLowerCase() === 'qr' ? 'cyan' : 'blue'}>
                    {comprobanteDetalle.ven_metodo_pago}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Total">
                  ${parseFloat(comprobanteDetalle.ven_total || 0).toFixed(2)}
                </Descriptions.Item>
                {comprobanteDetalle.ven_observaciones && (
                  <Descriptions.Item label="Observaciones">
                    {comprobanteDetalle.ven_observaciones}
                  </Descriptions.Item>
                )}
              </Descriptions>
              <Divider>Artículos</Divider>
              <Table
                columns={[
                  { title: 'Artículo', dataIndex: 'articulo_nombre', key: 'articulo_nombre' },
                  { title: 'Cantidad', dataIndex: 'ved_cantidad', key: 'ved_cantidad' },
                  {
                    title: 'Precio Unit.',
                    dataIndex: 'ved_precio_unitario',
                    key: 'ved_precio_unitario',
                    render: (precio) => `$${parseFloat(precio).toFixed(2)}`,
                  },
                  {
                    title: 'Subtotal',
                    dataIndex: 'ved_subtotal',
                    key: 'ved_subtotal',
                    render: (subtotal) => `$${parseFloat(subtotal).toFixed(2)}`,
                  },
                ]}
                dataSource={comprobanteDetalle.detalles || []}
                pagination={false}
                size="small"
              />
            </>
          ) : movimientoSeleccionado ? (
            <Descriptions column={1} bordered>
              <Descriptions.Item label="Fecha">
                {dayjs(movimientoSeleccionado.mos_fecha).format('DD/MM/YYYY HH:mm')}
              </Descriptions.Item>
              <Descriptions.Item label="Artículo">
                {movimientoSeleccionado.articulo_nombre}
              </Descriptions.Item>
              <Descriptions.Item label="Tipo">
                <Tag color={movimientoSeleccionado.mos_tipo_movimiento === 'compra' ? 'green' : movimientoSeleccionado.mos_tipo_movimiento === 'venta' ? 'red' : 'blue'}>
                  {movimientoSeleccionado.mos_tipo_movimiento?.toUpperCase()}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Cantidad">
                <span style={{ color: movimientoSeleccionado.mos_cantidad > 0 ? '#52c41a' : '#ff4d4f', fontWeight: 'bold' }}>
                  {movimientoSeleccionado.mos_cantidad > 0 ? '+' : ''}{movimientoSeleccionado.mos_cantidad}
                </span>
              </Descriptions.Item>
              {movimientoSeleccionado.mos_observaciones && (
                <Descriptions.Item label="Observaciones">
                  {movimientoSeleccionado.mos_observaciones}
                </Descriptions.Item>
              )}
            </Descriptions>
          ) : null}
        </Modal>
      </div>
    </MainLayout>
  );
};

export default MovimientosStock;
